import React, { useState, useRef, useEffect } from 'react';
import styles from './ai-chat-bot.module.scss';
import { IDefaultResponsiveSupport } from '../../../../common/interfaces/common.interface';
import { qnaService } from '../../../../common/services/q-n-a.service';
import { useSystemSounds } from '../../../../hooks/system-sounds/useSystemSounds';

interface AIChatBotProps extends IDefaultResponsiveSupport {}

interface Message {
  id: number;
  text: string;
  sender: 'user' | 'ai';
}

const THINKING_TIME = 1200;

const AIChatBot: React.FC<AIChatBotProps> = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const { play } = useSystemSounds();

  const bottomRef = useRef<HTMLDivElement>(null);
  const idRef = useRef(0);

  const scrollBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(scrollBottom, [messages, thinking]);

  const sendMessage = () => {
    if (!input.trim() || thinking) return;
    play('click');
    const userMsg: Message = {
      id: idRef.current++,
      text: input,
      sender: 'user',
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setThinking(true);

    // mimic AI thinking
    setTimeout(
      () => {
        const response = qnaService.getBestResponse(userMsg.text);

        const aiMsg: Message = {
          id: idRef.current++,
          text: response,
          sender: 'ai',
        };

        setMessages((prev) => [...prev, aiMsg]);
        setThinking(false);
      },
      THINKING_TIME + Math.random() * 800,
    );
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') sendMessage();
  };

  return (
    <div className={styles.chatRoot}>
      {/* Messages */}
      <div className={styles.messages}>
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`${styles.message} ${
              msg.sender === 'user' ? styles.user : styles.ai
            }`}
          >
            {msg.text}
          </div>
        ))}

        {thinking && (
          <div className={`${styles.message} ${styles.ai}`}>
            <span className={styles.thinking}>Thinking...</span>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className={styles.inputArea}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder="Ask me anything..."
        />

        <button onClick={sendMessage}>Send</button>
      </div>
    </div>
  );
};

export default AIChatBot;
